import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import { useVirtualizer } from "@tanstack/react-virtual";
import EntryForm from "components/EntryForm";
import CommentComposer from "components/CommentComposer";
import UpvoteModal from "components/UpvoteModal";
import {
  MessageSquare, FileText, Check, X, ExternalLink, RefreshCw, Loader2, Edit2, Trash2,
  Plus, Copy, CheckCircle, ArrowUp, ArrowDown, ArrowUpDown, User, Hash, Calendar, Download as DownloadIcon,
  TrendingUp, Zap, RotateCcw, Eye, Send, ThumbsUp, Star
} from "lucide-react";

const COLUMNS = [
  { key: "marketer_name", label: "Marketer", width: 120 },
  { key: "entry_type", label: "Type", width: 45 },
  { key: "account", label: "Account", width: 130 },
  { key: "subreddit", label: "Subreddit", width: 140 },
  { key: "status", label: "Status", width: 110 },
  { key: "upvotes", label: "Upvotes", width: 75 },
  { key: "rank_in_post", label: "Rank", width: 60 },
  { key: "keyword", label: "Keyword", width: 120 },
  { key: "traffic_vol", label: "Org. Traffic", width: 90 },
  { key: "grade", label: "Grade", width: 65 },
  { key: "views", label: "Views", width: 75 },
  { key: "check", label: "Check", width: 55 },
  { key: "url", label: "URL", width: 200 },
  { key: "actions", label: "", width: 80 },
];

const ROW_HEIGHT = 44;

export default function EntriesTable({ projects, selectedProject, marketers, onRefresh, refreshKey }) {
  const { api, isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [marketerFilter, setMarketerFilter] = useState("");
  const [rankFilter, setRankFilter] = useState("all");
  const [searchAccount, setSearchAccount] = useState("");
  const [searchSubreddit, setSearchSubreddit] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [checkingId, setCheckingId] = useState({});
  const [copied, setCopied] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [view, setView] = useState("entries");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all"); // kept for API compatibility
  const [accountStats, setAccountStats] = useState([]);
  const [subredditStats, setSubredditStats] = useState([]);
  const [keywordStats, setKeywordStats] = useState([]);
  const [fetchingTraffic, setFetchingTraffic] = useState({});
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editingViews, setEditingViews] = useState(null);
  const [viewsInput, setViewsInput] = useState("");
  const [fetchingViews, setFetchingViews] = useState(false);
  const [upvoting, setUpvoting] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [showUpvoteModal, setShowUpvoteModal] = useState(false);
  const [gradingId, setGradingId] = useState({});
  const [gradeModal, setGradeModal] = useState(null);
  const [bulkGrading, setBulkGrading] = useState(false);
  const [kwSort, setKwSort] = useState({ key: "organic_traffic", dir: "desc" });

  const parentRef = useRef(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProject) params.project_id = selectedProject;
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.entry_type = typeFilter;
      if (marketerFilter) params.marketer_name = marketerFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      params.limit = PAGE_SIZE;
      params.skip = page * PAGE_SIZE;
      if (sort.key) {
        params.sort_by = sort.key;
        params.sort_order = sort.dir;
      }
      const res = await api.get("/entries", { params });
      const data = res.data;
      if (data && data.entries) {
        setEntries(data.entries);
        setTotalEntries(data.total || 0);
      } else if (Array.isArray(data)) {
        // Backward compatibility
        setEntries(data);
        setTotalEntries(data.length);
      } else {
        setEntries([]);
        setTotalEntries(0);
      }
    } catch (err) {
      console.error("Entries fetch error:", err);
      setEntries([]);
      setTotalEntries(0);
    } finally {
      setLoading(false);
    }
  }, [api, selectedProject, statusFilter, typeFilter, marketerFilter, sourceFilter, page, sort]);

  useEffect(() => { setPage(0); }, [statusFilter, typeFilter, marketerFilter, sourceFilter, selectedProject]);

  useEffect(() => {
    if (loading) {
      const t = setTimeout(() => setLoading(false), 10000);
      return () => clearTimeout(t);
    }
  }, [loading]);

  const fetchGroupedStats = useCallback(async () => {
    const params = selectedProject ? { project_id: selectedProject } : {};
    try {
      const [accRes, subRes, kwRes] = await Promise.all([
        api.get("/stats/by-account", { params }),
        api.get("/stats/by-subreddit", { params }),
        api.get("/stats/by-keyword", { params }),
      ]);
      setAccountStats(accRes.data);
      setSubredditStats(subRes.data);
      setKeywordStats(kwRes.data);
    } catch {}
  }, [api, selectedProject]);

  useEffect(() => { fetchEntries(); fetchGroupedStats(); }, [fetchEntries, fetchGroupedStats]);

  const sortedKeywords = useMemo(() => {
    if (!kwSort.key) return keywordStats;
    return [...keywordStats].sort((a, b) => {
      let av = a[kwSort.key], bv = b[kwSort.key];
      if (av == null) av = kwSort.dir === "asc" ? Infinity : -Infinity;
      if (bv == null) bv = kwSort.dir === "asc" ? Infinity : -Infinity;
      if (typeof av === "string") return kwSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return kwSort.dir === "asc" ? av - bv : bv - av;
    });
  }, [keywordStats, kwSort]);

  const handleKwSort = (key) => {
    setKwSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };


  useEffect(() => {
    if (refreshKey > 0) { fetchEntries(); fetchGroupedStats(); }
  }, [refreshKey]); // eslint-disable-line

  const filteredAndSorted = useMemo(() => {
    let result = entries;
    if (searchAccount) result = result.filter(e => e.account?.toLowerCase().includes(searchAccount.toLowerCase()));
    if (searchSubreddit) result = result.filter(e => e.subreddit?.toLowerCase().includes(searchSubreddit.toLowerCase()));
    if (rankFilter !== "all") {
      const maxRank = parseInt(rankFilter);
      result = result.filter(e => e.rank_in_post && e.rank_in_post <= maxRank);
    }
    if (dateFrom) result = result.filter(e => (e.date || e.created_at || "") >= dateFrom);
    if (dateTo) result = result.filter(e => (e.date || e.created_at || "") <= dateTo + "T23:59:59");
    if (sort.key) {
      result = [...result].sort((a, b) => {
        let av, bv;
        if (sort.key === 'traffic_vol') {
          av = a.keywords_data?.estimated_traffic ?? a.keyword_analysis?.estimated_traffic ?? 0;
          bv = b.keywords_data?.estimated_traffic ?? b.keyword_analysis?.estimated_traffic ?? 0;
        } else if (sort.key === 'grade') {
          av = a.grade?.overall_score ?? -1;
          bv = b.grade?.overall_score ?? -1;
        } else if (sort.key === 'keyword') {
          av = a.keywords_data?.top_keyword || a.keyword || '';
          bv = b.keywords_data?.top_keyword || b.keyword || '';
        } else { av = a[sort.key]; bv = b[sort.key]; }
        if (av == null) av = sort.dir === "asc" ? Infinity : -Infinity;
        if (bv == null) bv = sort.dir === "asc" ? Infinity : -Infinity;
        if (typeof av === "string") return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        return sort.dir === "asc" ? av - bv : bv - av;
      });
    }
    return result;
  }, [entries, searchAccount, searchSubreddit, rankFilter, dateFrom, dateTo, sort]);

  const rowVirtualizer = useVirtualizer({
    count: filteredAndSorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  const handleSort = (key) => {
    if (["check", "actions", "url"].includes(key)) return;
    setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    await api.delete(`/entries/${id}`);
    fetchEntries(); onRefresh?.();
  };

  const handleStatusChange = async (id, status, currentStatus) => {
    if (status === currentStatus) return;
    if (!window.confirm(`Change status from "${currentStatus}" to "${status}"?`)) return;
    await api.patch(`/entries/${id}`, { status });
    fetchEntries();
  };

  const handleCheck = async (id) => {
    setCheckingId(p => ({ ...p, [id]: true }));
    try { await api.post(`/entries/${id}/check-status`); fetchEntries(); } catch {} finally {
      setCheckingId(p => ({ ...p, [id]: false }));
    }
  };

  const handleBulkStatus = async (status) => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Change ${selectedIds.length} entries to "${status}"?`)) return;
    await api.post("/entries/bulk-status", { entry_ids: selectedIds, status });
    setSelectedIds([]); fetchEntries();
  };

  const handleBulkAssign = async (projectId) => {
    if (!selectedIds.length) return;
    await api.post("/entries/assign-project", { project_id: projectId, entry_ids: selectedIds });
    setSelectedIds([]); fetchEntries();
  };

  const handleBulkAssignMarketer = async (marketerName) => {
    if (!selectedIds.length || !marketerName) return;
    await api.post("/entries/bulk-assign-marketer", { entry_ids: selectedIds, marketer_name: marketerName });
    setSelectedIds([]); fetchEntries();
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Permanently delete ${selectedIds.length} entries? This cannot be undone.`)) return;
    await api.post("/entries/bulk-delete", { entry_ids: selectedIds });
    setSelectedIds([]); fetchEntries(); onRefresh?.();
  };

  const [refetching, setRefetching] = useState(false);
  const handleBulkRefetch = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Re-fetch Reddit stats (upvotes, rank, status) for ${selectedIds.length} entries? This may take a while.`)) return;
    setRefetching(true);
    try {
      const res = await api.post("/entries/bulk-refetch", { entry_ids: selectedIds, status: "live" });
      alert(`Done! Checked: ${res.data.checked}, Updated: ${res.data.updated}, Errors: ${res.data.errors}`);
      setSelectedIds([]); fetchEntries(); onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Re-fetch failed");
    } finally { setRefetching(false); }
  };

  const handleResetDeleted = async () => {
    const deletedCount = entries.filter(e => e.status === "deleted").length || "all deleted";
    if (!window.confirm(`Reset ${deletedCount} deleted entries to "unknown" for re-checking?`)) return;
    setResetting(true);
    try {
      const res = await api.post("/entries/bulk-reset-to-unknown");
      alert(`Reset ${res.data.reset_count} entries to unknown`);
      fetchEntries(); onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const handleFetchTraffic = async (entryId) => {
    setFetchingTraffic(p => ({ ...p, [entryId]: true }));
    try {
      const res = await api.post(`/keywords/organic-single/${entryId}`);
      if (res.data?.task_id) {
        // Background task - keep spinner and poll for completion (~1 min)
        const taskId = res.data.task_id;
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const sr = await api.get(`/keywords/organic-traffic-status/${taskId}`);
            if (sr.data.status === "completed" || sr.data.status === "failed") {
              break;
            }
          } catch { break; }
        }
        fetchEntries();
      } else {
        // Cached result returned immediately
        fetchEntries();
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Traffic fetch failed");
    } finally { setFetchingTraffic(p => ({ ...p, [entryId]: false })); }
  };

  const handleBulkAnalyze = async () => {
    setBulkAnalyzing(true);
    try {
      const body = {};
      if (selectedProject) {
        body.project_id = selectedProject;
      } else if (selectedIds.length > 0) {
        body.entry_ids = selectedIds;
      } else {
        // No project and no selection — fetch for all live entries
        body.project_id = "__all__";
      }
      const res = await api.post("/keywords/organic-traffic", body);
      if (res.data?.task_id) {
        alert(`Organic traffic fetch started for ${res.data.queued_entries} entries. This runs in background (~1 min per unique post URL).`);
        const taskId = res.data.task_id;
        const poll = setInterval(async () => {
          try {
            const sr = await api.get(`/keywords/organic-traffic-status/${taskId}`);
            if (sr.data.status === "completed" || sr.data.status === "failed") {
              clearInterval(poll);
              fetchEntries();
              setBulkAnalyzing(false);
            }
          } catch { clearInterval(poll); setBulkAnalyzing(false); }
        }, 10000);
      } else {
        alert(res.data?.message || res.data?.error || "No entries to process");
        setBulkAnalyzing(false);
      }
    } catch (err) { alert(err.response?.data?.detail || "Organic traffic fetch failed"); setBulkAnalyzing(false); }
  };

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url.replace(/&amp;/g, '&'));
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleGradeEntry = async (entryId) => {
    setGradingId(prev => ({ ...prev, [entryId]: true }));
    try {
      const res = await api.post(`/llm/grade-entry/${entryId}`, {});
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, grade: res.data.grade } : e));
      setGradeModal(res.data.grade);
    } catch (err) { alert(err.response?.data?.detail || "Grading failed"); }
    finally { setGradingId(prev => ({ ...prev, [entryId]: false })); }
  };

  const handleBulkGrade = async () => {
    if (!selectedIds.length) return;
    setBulkGrading(true);
    try {
      const res = await api.post("/llm/bulk-grade", { entry_ids: selectedIds.slice(0, 20) });
      alert(`Graded ${res.data.graded} entries${res.data.errors ? `, ${res.data.errors} errors` : ''}`);
      fetchEntries();
    } catch (err) { alert(err.response?.data?.detail || "Bulk grading failed"); }
    finally { setBulkGrading(false); }
  };

  const handleSaveViews = async (entryId) => {
    const val = parseInt(viewsInput, 10);
    if (isNaN(val) || val < 0) { setEditingViews(null); return; }
    try {
      await api.post(`/settings/adspower/update-views?entry_id=${entryId}&views=${val}`);
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, views: val, views_updated_at: new Date().toISOString() } : e));
    } catch (err) {
      console.error("Failed to update views:", err);
    }
    setEditingViews(null);
  };

  const handleBridgeFetchViews = async () => {
    if (!selectedIds.length) return;
    setFetchingViews(true);
    try {
      await api.post("/bridge/enqueue", { command_type: "fetch_views", entry_ids: selectedIds });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to enqueue. Is the bridge running?");
    } finally {
      setFetchingViews(false);
    }
  };

  const handleUpvote = () => {
    if (!selectedIds.length) return;
    setShowUpvoteModal(true);
  };

  const allFilteredIds = useMemo(() => filteredAndSorted.map(e => e.id).filter(Boolean), [filteredAndSorted]);

  const visibleIds = useMemo(() => {
    return rowVirtualizer.getVirtualItems().map(vi => filteredAndSorted[vi.index]?.id).filter(Boolean);
  }, [rowVirtualizer.getVirtualItems(), filteredAndSorted]); // eslint-disable-line

  const toggleSelectAll = () => setSelectedIds(p => p.length === allFilteredIds.length ? [] : [...allFilteredIds]);
  const toggleSelect = (id) => setSelectedIds(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);

  const formatUrl = (url) => {
    if (!url) return "-";
    const c = url.replace(/&amp;/g, '&');
    return c.length > 30 ? c.substring(0, 30) + "..." : c;
  };

  const SortIcon = ({ col }) => {
    if (sort.key !== col) return <ArrowUpDown size={11} style={{ opacity: 0.3 }} />;
    return sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;
  };

  const totalWidth = COLUMNS.reduce((sum, c) => sum + c.width, 0) + 40;

  const renderRow = (e) => (
    <>
      <div style={{ width: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className={`custom-checkbox ${selectedIds.includes(e.id) ? 'checked' : ''}`} onClick={() => toggleSelect(e.id)}>
          {selectedIds.includes(e.id) && <Check size={12} color="white" />}
        </div>
      </div>
      <div style={{ width: 120, flexShrink: 0 }}><span className="marketer-cell">{e.marketer_name}</span></div>
      <div style={{ width: 45, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <span className={`type-badge ${e.entry_type?.toLowerCase()}`} title={e.entry_type || 'Comment'}>
          {e.entry_type === 'Post' ? <FileText size={14} /> : <MessageSquare size={14} />}
        </span>
      </div>
      <div style={{ width: 130, flexShrink: 0 }} className="account-cell">{e.account}</div>
      <div style={{ width: 140, flexShrink: 0 }}><span className="subreddit-cell">r/{e.subreddit}</span></div>
      <div style={{ width: 110, flexShrink: 0 }}>
        <select className={`status-select ${e.status}`} value={e.status}
          onChange={ev => handleStatusChange(e.id, ev.target.value, e.status)}>
          <option value="live">Live</option><option value="deleted">Deleted</option><option value="unknown">Unknown</option>
          <option value="duplicate">Duplicate</option>
          <option value="imported">To Be Assigned</option><option value="assigned">Assigned</option>
        </select>
      </div>
      <div style={{ width: 75, flexShrink: 0, fontFamily: 'JetBrains Mono', fontSize: 12, color: e.upvotes > 0 ? '#10b981' : '#6b7280' }}>{e.upvotes || 0}</div>
      <div style={{ width: 60, flexShrink: 0 }}>
        {e.rank_in_post ? <span className={`rank-badge ${e.rank_in_post <= 3 ? 'top' : e.rank_in_post <= 10 ? 'good' : ''}`}>#{e.rank_in_post}</span> : <span style={{ color: '#4b5563' }}>-</span>}
      </div>
      <div style={{ width: 120, flexShrink: 0, fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.keywords_data?.top_keyword || e.keyword || ''}>{e.keywords_data?.top_keyword || e.keyword || '-'}</div>
      <div style={{ width: 90, flexShrink: 0 }}>
        {(e.keywords_data?.analyzed_at || e.keyword_analysis?.analyzed_at) ? (
          <span className="traffic-badge" title={(() => {
            const kd = e.keywords_data || e.keyword_analysis || {};
            const urlKws = (kd.url_keywords || []).slice(0, 5);
            let tip = `Org. Traffic: ${kd.estimated_traffic ?? 0}/mo`;
            if (kd.traffic_value) tip += `\nTraffic Value: ${kd.traffic_value}`;
            if (kd.traffic_trend) tip += `\nTrend: ${kd.traffic_trend}`;
            if (kd.post_url) tip += `\nPost: ${kd.post_url}`;
            if (urlKws.length > 0) { tip += '\n\nTop Keywords:'; urlKws.forEach(k => { tip += `\n  ${k.keyword}: pos #${k.position || k.serp_position}, vol ${k.volume || k.estimated_monthly_traffic}`; }); }
            return tip;
          })()}>
            {(() => {
              const kd = e.keywords_data || e.keyword_analysis || {};
              const val = kd.estimated_traffic ?? kd.total_volume ?? 0;
              const isPost = (e.entry_type || "").toLowerCase() === "post";
              const trend = isPost ? kd.traffic_trend : null;
              const trendIcon = trend === 'up' ? ' \u25B2' : trend === 'down' ? ' \u25BC' : '';
              const formatted = val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val);
              return <>{formatted}{trendIcon && <span style={{ fontSize: 9, color: trend === 'up' ? '#34d399' : '#f87171' }}>{trendIcon}</span>}</>;
            })()}
          </span>
        ) : (
          <button className="btn-fetch-traffic" onClick={() => handleFetchTraffic(e.id)} disabled={fetchingTraffic[e.id]} data-testid={`fetch-traffic-${e.id}`} title="Fetch organic traffic data">
            {fetchingTraffic[e.id] ? <Loader2 size={11} className="animate-spin" /> : <TrendingUp size={11} />}
          </button>
        )}
      </div>
      {/* Grade column */}
      <div style={{ width: 65, flexShrink: 0, textAlign: 'center' }}>
        {gradingId[e.id] ? (
          <Loader2 size={13} className="animate-spin" style={{ color: '#a78bfa' }} />
        ) : e.grade ? (
          <span
            onClick={() => {
              // Fetch full grade with improved_version
              api.get(`/entries/${e.id}`).then(res => {
                setGradeModal(res.data?.grade || e.grade);
              }).catch(() => setGradeModal(e.grade));
            }}
            data-testid={`grade-badge-${e.id}`}
            style={{
              cursor: 'pointer',
              fontFamily: 'JetBrains Mono', fontWeight: 700, fontSize: 13,
              padding: '2px 8px', borderRadius: 4, display: 'inline-block',
              background: e.grade.overall_grade?.startsWith('A') ? '#065f4620' : e.grade.overall_grade?.startsWith('B') ? '#1e3a5f40' : e.grade.overall_grade?.startsWith('C') ? '#5f4b0640' : '#5f060620',
              color: e.grade.overall_grade?.startsWith('A') ? '#34d399' : e.grade.overall_grade?.startsWith('B') ? '#60a5fa' : e.grade.overall_grade?.startsWith('C') ? '#fbbf24' : '#f87171',
              border: `1px solid ${e.grade.overall_grade?.startsWith('A') ? '#34d39930' : e.grade.overall_grade?.startsWith('B') ? '#60a5fa30' : e.grade.overall_grade?.startsWith('C') ? '#fbbf2430' : '#f8717130'}`,
            }}
            title={`Score: ${e.grade.overall_score}/100\n${e.grade.verdict || ''}\nClick for details`}
          >
            {e.grade.overall_grade}
          </span>
        ) : (
          <button
            onClick={() => handleGradeEntry(e.id)}
            data-testid={`grade-btn-${e.id}`}
            style={{ background: 'none', border: '1px solid #4b556340', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#a78bfa', fontSize: 11 }}
            title="Grade with AI"
          >
            <Star size={12} />
          </button>
        )}
      </div>
      <div style={{ width: 75, flexShrink: 0 }}>
        {editingViews === e.id ? (
          <input
            autoFocus
            type="number"
            min="0"
            value={viewsInput}
            onChange={ev => setViewsInput(ev.target.value)}
            onBlur={() => handleSaveViews(e.id)}
            onKeyDown={ev => { if (ev.key === 'Enter') handleSaveViews(e.id); if (ev.key === 'Escape') setEditingViews(null); }}
            style={{ width: 60, padding: '2px 4px', fontSize: 12, background: '#27272a', border: '1px solid #3b82f6', borderRadius: 4, color: '#e5e5e5', fontFamily: 'JetBrains Mono', outline: 'none' }}
            data-testid={`views-input-${e.id}`}
          />
        ) : (
          <span
            onClick={() => { setEditingViews(e.id); setViewsInput(e.views != null ? String(e.views) : "0"); }}
            style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: e.views > 0 ? '#60a5fa' : '#4b5563', cursor: 'pointer', padding: '2px 4px', borderRadius: 3, display: 'inline-block', minWidth: 20 }}
            title={e.views > 0 ? `Views: ${e.views.toLocaleString()}${e.views_updated_at ? `\nUpdated: ${new Date(e.views_updated_at).toLocaleDateString()}` : ''}\nClick to edit` : 'Click to enter views'}
            data-testid={`views-cell-${e.id}`}
          >
            {e.views != null && e.views > 0 ? (e.views >= 1000 ? `${(e.views / 1000).toFixed(1)}k` : e.views) : '-'}
          </span>
        )}
      </div>
      <div style={{ width: 55, flexShrink: 0 }}>
        {e.url ? (
          <button className={`check-status-btn ${e.status}`} onClick={() => handleCheck(e.id)} disabled={checkingId[e.id]}>
            {checkingId[e.id] ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        ) : <span style={{ color: '#4b5563', fontSize: 11 }}>-</span>}
      </div>
      <div style={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
        {e.url ? (
          <>
            <a href={e.url.replace(/&amp;/g, '&')} target="_blank" rel="noopener noreferrer" className="url-link" title={e.url}>
              {formatUrl(e.url)}<ExternalLink size={9} style={{ marginLeft: 2 }} />
            </a>
            <button className="copy-btn" onClick={() => copyLink(e.url)} data-testid={`copy-link-${e.id}`}>
              {copied === e.url ? <CheckCircle size={12} color="#10b981" /> : <Copy size={12} />}
            </button>
          </>
        ) : <span style={{ color: '#4b5563', fontSize: 11 }}>-</span>}
      </div>
      <div style={{ width: 80, flexShrink: 0, display: 'flex', gap: 3 }}>
        <button className="btn btn-sm btn-secondary" onClick={() => { setEditEntry(e); setShowForm(true); }} title="Edit entry data" data-testid={`edit-entry-${e.id}`}><Edit2 size={12} /></button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(e.id)} data-testid={`delete-entry-${e.id}`}><Trash2 size={12} /></button>
      </div>
    </>
  );

  return (
    <div data-testid="entries-table-section">
      <div className="view-switcher" data-testid="view-switcher">
        <button className={`view-btn ${view === 'entries' ? 'active' : ''}`} onClick={() => setView('entries')}>
          <MessageSquare size={14} /> All Entries <span className="view-count">{filteredAndSorted.length}</span>
        </button>
        <button className={`view-btn ${view === 'accounts' ? 'active' : ''}`} onClick={() => setView('accounts')}>
          <User size={14} /> By Account <span className="view-count">{accountStats.length}</span>
        </button>
        <button className={`view-btn ${view === 'subreddits' ? 'active' : ''}`} onClick={() => setView('subreddits')}>
          <Hash size={14} /> By Subreddit <span className="view-count">{subredditStats.length}</span>
        </button>
        <button className={`view-btn ${view === 'keywords' ? 'active' : ''}`} onClick={() => setView('keywords')} data-testid="view-by-keyword">
          <TrendingUp size={14} /> By Keyword <span className="view-count">{keywordStats.length}</span>
        </button>
      </div>

      {view === "entries" && (
        <>
          <div className="filter-bar" data-testid="filter-bar">
            <div className="status-tabs">
              {[
                { value: "all", label: "All" },
                { value: "live", label: "Live" },
                { value: "deleted", label: "Deleted" },
                { value: "unknown", label: "Unknown" },
                { value: "duplicate", label: "Duplicate" },
                { value: "imported", label: "To Be Assigned" },
                { value: "assigned", label: "Assigned" },
              ].map(s => (
                <button key={s.value} className={`status-tab ${statusFilter === s.value ? `active ${s.value}` : ''}`}
                  onClick={() => setStatusFilter(s.value)} data-testid={`filter-${s.value}`}>{s.label}</button>
              ))}
            </div>
            <select className="filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="filter-type">
              <option value="all">All Types</option><option value="Post">Posts</option><option value="Comment">Comments</option>
            </select>
            <select className="filter-select" value={marketerFilter} onChange={e => setMarketerFilter(e.target.value)} data-testid="filter-marketer">
              <option value="">All Marketers</option>
              {marketers.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className="filter-select" value={rankFilter} onChange={e => setRankFilter(e.target.value)} data-testid="filter-rank">
              <option value="all">All Ranks</option><option value="3">Top 3</option><option value="10">Top 10</option><option value="25">Top 25</option>
            </select>
            <input type="text" className="filter-input" placeholder="Account..." value={searchAccount}
              onChange={e => setSearchAccount(e.target.value)} data-testid="search-account" />
            <input type="text" className="filter-input" placeholder="Subreddit..." value={searchSubreddit}
              onChange={e => setSearchSubreddit(e.target.value)} data-testid="search-subreddit" />
            <div className="date-filter-group" data-testid="date-filter">
              <Calendar size={13} className="date-icon" />
              <input type="date" className="filter-input date-input" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} data-testid="date-from" title="From date" />
              <span className="date-sep">-</span>
              <input type="date" className="filter-input date-input" value={dateTo}
                onChange={e => setDateTo(e.target.value)} data-testid="date-to" title="To date" />
            </div>
            <button className="btn btn-primary" onClick={() => { setEditEntry(null); setShowForm(true); }} data-testid="add-entry-btn">
              <Plus size={14} /> Add
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleBulkAnalyze} disabled={bulkAnalyzing}
              data-testid="bulk-analyze-btn" title="Fetch organic traffic for entries without data">
              {bulkAnalyzing ? <Loader2 size={13} className="animate-spin" /> : <TrendingUp size={13} />}
              {bulkAnalyzing ? ' Fetching...' : ' Organic Traffic'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleBulkGrade} disabled={bulkGrading || !selectedIds.length}
              data-testid="bulk-grade-btn" title="Grade selected entries with AI" style={{ borderColor: '#a78bfa33', color: '#a78bfa' }}>
              {bulkGrading ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />}
              {bulkGrading ? ' Grading...' : ` Grade (${selectedIds.length})`}
            </button>
            {isAdmin && (
              <button className="btn btn-sm btn-secondary" onClick={handleResetDeleted} disabled={resetting}
                data-testid="reset-deleted-btn" title="Reset all deleted entries to unknown for re-checking"
                style={{ borderColor: '#f59e0b33', color: '#f59e0b' }}>
                {resetting ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                {resetting ? ' Resetting...' : ' Reset Deleted'}
              </button>
            )}
          </div>

          {selectedIds.length > 0 && (
            <div className="bulk-actions-bar" data-testid="bulk-actions">
              <div className="bulk-actions">
                <span className="bulk-count">{selectedIds.length} selected</span>
                {selectedIds.length < allFilteredIds.length && (
                  <button className="btn btn-sm" style={{color:'#ff4500',borderColor:'#ff450055',fontSize:12}} onClick={() => setSelectedIds([...allFilteredIds])} data-testid="select-all-matching-btn">
                    Select all {allFilteredIds.length}
                  </button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={() => handleBulkStatus("live")}><Check size={14} /> Live</button>
                <button className="btn btn-sm btn-secondary" onClick={() => handleBulkStatus("deleted")}><X size={14} /> Deleted</button>
                <button className="btn btn-sm btn-danger" onClick={handleBulkDelete} data-testid="bulk-delete-btn"><Trash2 size={14} /> Delete</button>
                <button className="btn btn-sm" style={{background:'#1e3a5f',color:'#60a5fa',borderColor:'#3b82f655'}} onClick={handleBulkRefetch} disabled={refetching} data-testid="bulk-refetch-btn">
                  {refetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  {refetching ? ' Fetching...' : ' Re-fetch Stats'}
                </button>
                <button className="btn btn-sm" style={{background:'#1e2a1f',color:'#10b981',borderColor:'#10b98155'}} onClick={handleBridgeFetchViews} disabled={fetchingViews} data-testid="bridge-fetch-views-btn">
                  {fetchingViews ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  {fetchingViews ? ' Queuing...' : ' Fetch Views'}
                </button>
                <button className="btn btn-sm" style={{background:'#1f1530',color:'#a78bfa',borderColor:'#7c3aed55'}} onClick={() => setShowComposer(true)} data-testid="post-comment-btn">
                  <Send size={14} /> Post Comment
                </button>
                <button className="btn btn-sm" style={{background:'#1a2a1f',color:'#f97316',borderColor:'#f9731655'}} onClick={handleUpvote} data-testid="upvote-btn">
                  <ThumbsUp size={14} /> Boost
                </button>
                {isAdmin && projects.length > 0 && (
                  <select className="filter-select assign-project-select" defaultValue=""
                    onChange={e => { if (e.target.value) { handleBulkAssign(e.target.value); e.target.value = ''; } }}>
                    <option value="" disabled>Assign to Project...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                  </select>
                )}
                <select className="filter-select assign-project-select" defaultValue=""
                  onChange={e => { if (e.target.value) { handleBulkAssignMarketer(e.target.value); e.target.value = ''; } }}
                  data-testid="bulk-assign-marketer">
                  <option value="" disabled>Assign to Marketer...</option>
                  {marketers.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <button className="btn btn-sm btn-secondary" onClick={() => setSelectedIds([])}>Clear</button>
              </div>
            </div>
          )}

          <div className="vtable-container" data-testid="entries-table">
            {loading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : filteredAndSorted.length === 0 ? (
              <div className="empty-state"><MessageSquare size={48} /><div className="empty-text">No entries found</div></div>
            ) : (
              <>
                <div className="table-scroll-wrapper">
                  {/* Header */}
                  <div style={{ display: 'flex', minWidth: totalWidth, borderBottom: '1px solid #27272a', padding: '8px 0', position: 'sticky', top: 0, background: '#0a0a0a', zIndex: 2 }}>
                    <div style={{ width: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div className={`custom-checkbox ${selectedIds.length > 0 && selectedIds.length === allFilteredIds.length ? 'checked' : selectedIds.length > 0 ? 'partial' : ''}`} onClick={toggleSelectAll}
                        title={selectedIds.length === allFilteredIds.length ? 'Deselect all' : `Select all ${allFilteredIds.length} matching entries`}>
                        {selectedIds.length > 0 && selectedIds.length === allFilteredIds.length && <Check size={12} color="white" />}
                        {selectedIds.length > 0 && selectedIds.length < allFilteredIds.length && <span style={{color:'white',fontSize:9,fontWeight:700}}>-</span>}
                      </div>
                    </div>
                    {COLUMNS.map(col => (
                      <div key={col.key} style={{
                        width: col.width, flexShrink: 0, padding: '4px 6px', fontSize: 11, fontWeight: 700,
                        color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em',
                        cursor: ['check', 'actions', 'url'].includes(col.key) ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        background: sort.key === col.key ? '#18181b' : 'transparent',
                      }} onClick={() => handleSort(col.key)}>
                        {col.label} {!['check', 'actions', 'url'].includes(col.key) && col.label && <SortIcon col={col.key} />}
                      </div>
                    ))}
                  </div>

                  {/* Virtual rows */}
                  <div ref={parentRef} style={{ height: Math.min(filteredAndSorted.length * ROW_HEIGHT, 600), overflow: 'auto', minWidth: totalWidth }}>
                    <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                      {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const e = filteredAndSorted[virtualRow.index];
                        if (!e) return null;
                        return (
                          <div key={e.id} data-testid={`entry-row-${e.id}`} style={{
                            position: 'absolute', top: 0, left: 0, width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                            display: 'flex', alignItems: 'center',
                            borderBottom: '1px solid #1a1a1e',
                            padding: '0',
                          }} className="virtual-row">
                            {renderRow(e)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="vtable-footer" data-testid="table-footer">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Showing {filteredAndSorted.length} of {totalEntries} entries</span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn btn-sm btn-secondary" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="page-prev">Prev</button>
                      <span style={{ fontSize: 12, fontFamily: 'JetBrains Mono', color: '#a1a1aa' }}>
                        Page {page + 1} of {Math.max(1, Math.ceil(totalEntries / PAGE_SIZE))}
                      </span>
                      <button className="btn btn-sm btn-secondary" disabled={(page + 1) * PAGE_SIZE >= totalEntries} onClick={() => setPage(p => p + 1)} data-testid="page-next">Next</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {view === "accounts" && (
        <div className="grouped-table" data-testid="accounts-view">
          <table className="comments-table">
            <thead><tr>
              <th>#</th><th>Account</th><th>Entries</th><th>Live</th><th>Deleted</th><th>Success %</th><th>Upvotes</th><th>Subreddits</th><th>Score</th><th>Marketers</th>
            </tr></thead>
            <tbody>
              {accountStats.map((a) => (
                <tr key={a.account}>
                  <td><span className={`rank-badge ${a.rank <= 3 ? 'top' : a.rank <= 10 ? 'good' : ''}`}>#{a.rank}</span></td>
                  <td><span className="account-cell">{a.account}</span></td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{a.total}</td>
                  <td style={{ color: 'var(--live-green)', fontFamily: 'JetBrains Mono' }}>{a.live}</td>
                  <td style={{ color: 'var(--deleted-red)', fontFamily: 'JetBrains Mono' }}>{a.deleted}</td>
                  <td>{a.success_rate}%</td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{a.upvotes}</td>
                  <td>{a.subreddits}</td>
                  <td><span className="score-badge">{a.score}</span></td>
                  <td style={{ fontSize: 12, color: '#9ca3af' }}>{a.marketers?.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "subreddits" && (
        <div className="grouped-table" data-testid="subreddits-view">
          <table className="comments-table">
            <thead><tr>
              <th>#</th><th>Subreddit</th><th>Entries</th><th>Live</th><th>Deleted</th><th>Success %</th><th>Upvotes</th><th>Accounts</th><th>Score</th><th>Marketers</th>
            </tr></thead>
            <tbody>
              {subredditStats.map((s) => (
                <tr key={s.subreddit}>
                  <td><span className={`rank-badge ${s.rank <= 3 ? 'top' : s.rank <= 10 ? 'good' : ''}`}>#{s.rank}</span></td>
                  <td><span className="subreddit-cell">r/{s.subreddit}</span></td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{s.total}</td>
                  <td style={{ color: 'var(--live-green)', fontFamily: 'JetBrains Mono' }}>{s.live}</td>
                  <td style={{ color: 'var(--deleted-red)', fontFamily: 'JetBrains Mono' }}>{s.deleted}</td>
                  <td>{s.success_rate}%</td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{s.upvotes}</td>
                  <td>{s.accounts}</td>
                  <td><span className="score-badge">{s.score}</span></td>
                  <td style={{ fontSize: 12, color: '#9ca3af' }}>{s.marketers?.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "keywords" && (
        <div className="grouped-table" data-testid="keywords-view">
          <table className="comments-table">
            <thead><tr>
              <th style={{width:40}}>#</th>
              {[
                { key: "keyword", label: "Keyword" },
                { key: "entries", label: "Entries" },
                { key: "live", label: "Live" },
                { key: "organic_traffic", label: "Org. Traffic" },
                { key: "traffic_value_num", label: "Traffic Value" },
                { key: "traffic_trend", label: "Trend" },
                { key: "avg_position", label: "Avg Position" },
                { key: "avg_volume", label: "Volume" },
                { key: null, label: "Subreddits" },
              ].map(col => (
                <th key={col.label} style={{ cursor: col.key ? 'pointer' : 'default', userSelect: 'none' }}
                  onClick={() => col.key && handleKwSort(col.key)}>
                  {col.label}
                  {col.key && kwSort.key === col.key && (kwSort.dir === "asc" ? <ArrowUp size={10} style={{marginLeft:3}}/> : <ArrowDown size={10} style={{marginLeft:3}}/>)}
                  {col.key && kwSort.key !== col.key && <ArrowUpDown size={10} style={{marginLeft:3, opacity: 0.3}}/>}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {sortedKeywords.map((k, i) => (
                <tr key={k.keyword}>
                  <td><span className={`rank-badge ${(i+1) <= 3 ? 'top' : (i+1) <= 10 ? 'good' : ''}`}>#{i+1}</span></td>
                  <td title={k.keyword}><strong style={{ color: '#e5e5e5', fontSize: 13, cursor: 'help' }}>{k.keyword}</strong></td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{k.entries}</td>
                  <td style={{ color: 'var(--live-green)', fontFamily: 'JetBrains Mono' }}>{k.live}</td>
                  <td style={{ fontFamily: 'JetBrains Mono', color: k.organic_traffic > 0 ? '#34d399' : '#71717a' }}>{k.organic_traffic?.toLocaleString()}</td>
                  <td style={{ fontFamily: 'JetBrains Mono', color: '#fbbf24' }}>{k.traffic_value || '-'}</td>
                  <td>
                    {k.traffic_trend === 'up' && <span style={{ color: '#34d399', fontSize: 12 }}>&#9650; Up</span>}
                    {k.traffic_trend === 'down' && <span style={{ color: '#f87171', fontSize: 12 }}>&#9660; Down</span>}
                    {k.traffic_trend === 'stable' && <span style={{ color: '#71717a', fontSize: 12 }}>&#8212; Stable</span>}
                    {!k.traffic_trend && <span style={{ color: '#52525b' }}>-</span>}
                  </td>
                  <td>{k.avg_position ? <span className={`rank-badge ${k.avg_position <= 3 ? 'top' : k.avg_position <= 10 ? 'good' : ''}`}>#{k.avg_position}</span> : '-'}</td>
                  <td style={{ fontFamily: 'JetBrains Mono' }}>{k.avg_volume?.toLocaleString() || '-'}</td>
                  <td style={{ fontSize: 11, color: '#9ca3af' }}>{k.subreddits?.map(s => `r/${s}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {keywordStats.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#52525b' }}>No keyword data yet. Run Organic traffic fetch first.</div>
          )}
        </div>
      )}

      {showForm && (
        <EntryForm entry={editEntry} projects={projects} marketers={marketers}
          onClose={() => { setShowForm(false); setEditEntry(null); }}
          onSaved={() => { fetchEntries(); fetchGroupedStats(); onRefresh?.(); }} />
      )}

      {showComposer && (
        <CommentComposer
          entries={entries.filter(e => selectedIds.includes(e.id))}
          onClose={() => setShowComposer(false)}
          onSent={() => { setShowComposer(false); fetchEntries(); }}
        />
      )}

      {showUpvoteModal && (
        <UpvoteModal
          entries={entries.filter(e => selectedIds.includes(e.id))}
          onClose={() => setShowUpvoteModal(false)}
          onDone={() => { setShowUpvoteModal(false); }}
        />
      )}

      {gradeModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000088', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setGradeModal(null)} data-testid="grade-modal-overlay">
          <div style={{ background: '#1a1a2e', border: '1px solid #2d2d44', borderRadius: 12, width: 600, maxHeight: '80vh', overflow: 'auto', padding: 24 }}
            onClick={e => e.stopPropagation()} data-testid="grade-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#e5e5e5' }}>AI Quality Grade</h3>
              <button onClick={() => setGradeModal(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}><X size={18} /></button>
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <div style={{
                fontSize: 42, fontWeight: 800, fontFamily: 'JetBrains Mono',
                color: gradeModal.overall_grade?.startsWith('A') ? '#34d399' : gradeModal.overall_grade?.startsWith('B') ? '#60a5fa' : gradeModal.overall_grade?.startsWith('C') ? '#fbbf24' : '#f87171',
                lineHeight: 1,
              }}>
                {gradeModal.overall_grade}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>Score: {gradeModal.overall_score}/100</div>
                <div style={{ fontSize: 13, color: '#d1d5db' }}>{gradeModal.verdict}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
              {[
                { label: 'Helpfulness', key: 'helpfulness', icon: '?' },
                { label: 'Human Feel', key: 'human_feel', icon: '?' },
                { label: 'Reddit Vibe', key: 'reddit_vibe', icon: '?' },
                { label: 'Subtlety', key: 'subtlety', icon: '?' },
                { label: 'Website Mention', key: 'website_mention', icon: '?' },
                { label: 'Detection Risk', key: 'detection_risk', icon: '?' },
              ].map(({ label, key }) => {
                const val = gradeModal[key] || 0;
                const color = val >= 8 ? '#34d399' : val >= 6 ? '#60a5fa' : val >= 4 ? '#fbbf24' : '#f87171';
                return (
                  <div key={key} style={{ background: '#0f0f1a', borderRadius: 8, padding: '8px 12px', border: '1px solid #2d2d44' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'JetBrains Mono', color }}>{val}/10</div>
                  </div>
                );
              })}
            </div>

            {gradeModal.original_text && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6, fontWeight: 600 }}>ORIGINAL TEXT</div>
                <div style={{ background: '#0f0f1a', borderRadius: 8, padding: 12, border: '1px solid #2d2d44', fontSize: 13, color: '#d1d5db', lineHeight: 1.5, maxHeight: 150, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                  {gradeModal.original_text}
                </div>
              </div>
            )}

            {gradeModal.improved_version && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, color: '#34d399', fontWeight: 600 }}>SUGGESTED IMPROVEMENT</div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(gradeModal.improved_version); }}
                    style={{ background: 'none', border: '1px solid #34d39930', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#34d399', fontSize: 11 }}
                    data-testid="copy-improved-btn"
                  >
                    <Copy size={11} /> Copy
                  </button>
                </div>
                <div style={{ background: '#0a1a0f', borderRadius: 8, padding: 12, border: '1px solid #34d39920', fontSize: 13, color: '#d1d5db', lineHeight: 1.5, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                  {gradeModal.improved_version}
                </div>
              </div>
            )}

            <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'right' }}>
              Graded: {gradeModal.graded_at ? new Date(gradeModal.graded_at).toLocaleString() : '-'} | Model: {gradeModal.model_used || '-'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
